import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { login as apiLogin, getMe } from "../api/client";
import { hydratePrefs, flushPrefsNow } from "../utils/layoutStore";

const AuthContext = createContext(null);

function loadUser() {
  try { return JSON.parse(localStorage.getItem("sc_user") || "null"); }
  catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(loadUser);
  const [token, setToken] = useState(() => localStorage.getItem("sc_token") || null);

  const signIn = useCallback(async (username, password) => {
    const res = await apiLogin(username, password);
    const { access_token, user: u } = res.data;
    localStorage.setItem("sc_token", access_token);
    localStorage.setItem("sc_user", JSON.stringify(u));
    setToken(access_token);
    setUser(u);
    hydratePrefs();          // چیدمانِ جدول‌های همین کاربر از سرور (هر دستگاهی)
    return u;
  }, []);

  /* 🔴 مجوزها فقط لحظهٔ ورود در localStorage نوشته می‌شدند و **هرگز** تازه
     نمی‌شدند. اگر مدیر دسترسیِ کسی را عوض می‌کرد، منوی او تا خروج و ورودِ
     دوباره همان قبلی می‌ماند: آیتمی را می‌دید که سرور به آن ۴۰۳ می‌داد، کلیک
     می‌کرد، «خطا» می‌گرفت و «تلاش مجدد» هم هیچ‌وقت جواب نمی‌داد.
     حالا هر بار که برنامه باز می‌شود، مجوزها از سرور تازه می‌شوند. */
  useEffect(() => {
    if (!token) return;
    let alive = true;
    getMe()
      .then((r) => {
        if (!alive || !r?.data) return;
        localStorage.setItem("sc_user", JSON.stringify(r.data));
        setUser(r.data);
        hydratePrefs();      // چیدمانِ جدول‌ها از سرور (دستگاه/مرورگرِ دیگر هم همان)
      })
      // شکستِ شبکه نباید کاربر را بیرون بیندازد؛ همان نسخهٔ ذخیره‌شده می‌ماند
      .catch(() => {});
    return () => { alive = false; };
  }, [token]);

  const signOut = useCallback(() => {
    flushPrefsNow();       // آخرین تغییرِ چیدمان قبل از رفتنِ توکن (توکن همین لحظه برداشته می‌شود)
    localStorage.removeItem("sc_token");
    localStorage.removeItem("sc_user");
    setToken(null);
    setUser(null);
  }, []);

  // آیا کاربرِ جاری این مجوز را دارد؟
  const can = useCallback(
    (perm) => !!user && Array.isArray(user.permissions) && user.permissions.includes(perm),
    [user]
  );
  // کمک‌کننده‌های ماژولی — module مثلِ "production"/"payroll"/"supply_chain"/...
  const canView = useCallback((m) => can(`${m}.view`), [can]);
  const canCreate = useCallback((m) => can(`${m}.create`), [can]);
  const canEdit = useCallback((m) => can(`${m}.edit`), [can]);
  const canApprove = useCallback((m) => can(`${m}.approve`), [can]);
  const canViewAmounts = useCallback(
    (m) => can("amounts.view_all") || can(`amounts.view_${m}`), [can]);

  return (
    <AuthContext.Provider value={{
      user, token, isAuthed: !!token, signIn, signOut,
      can, canView, canCreate, canEdit, canApprove, canViewAmounts,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
